import { Children, type ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import { Column, Columns } from "./Columns";
import type { DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";
import { resolveGapClasses, type Space } from "./foundations/space";
import { SectionCard } from "./SectionCard";
import { Stack } from "./Stack";

const FORM_ALIGN_CLASSES = {
  stretch: undefined,
  start: "items-start",
} as const;

export type FormRootProps = {
  children: ReactNode;
  align?: keyof typeof FORM_ALIGN_CLASSES;
  fullWidth?: boolean;
  gap?: Space;
  maxHeight?: "parent";
} & DesignSystemElementProps<"form"> &
  DesignSystemRef<HTMLFormElement>;

export function FormRoot({
  align = "stretch",
  children,
  fullWidth = false,
  gap,
  maxHeight,
  ref,
  ...nativeProps
}: FormRootProps): ReactNode {
  return (
    <form
      {...nativeProps}
      ref={ref}
      className={twJoin(
        "flex flex-1 flex-col",
        FORM_ALIGN_CLASSES[align],
        fullWidth && "w-full",
        resolveGapClasses(gap),
        maxHeight === "parent" && "max-h-full",
      )}
    >
      {children}
    </form>
  );
}

export type FormContainerProps = {
  children: ReactNode;
  title?: string;
};

export function FormContainer({ children, title }: FormContainerProps): ReactNode {
  return <SectionCard title={title}>{children}</SectionCard>;
}

export type FormBodyProps = {
  children: ReactNode;
  "data-testid"?: string;
};

export function FormBody({ children, "data-testid": dataTestId }: FormBodyProps): ReactNode {
  return (
    <Stack gap="md" data-testid={dataTestId}>
      {children}
    </Stack>
  );
}

export type FormFooterProps = {
  renderPrimaryAction: () => ReactNode;
  renderSecondaryAction?: () => ReactNode;
  renderTertiaryAction?: () => ReactNode;
};

export function FormFooter({
  renderPrimaryAction,
  renderSecondaryAction,
  renderTertiaryAction,
}: FormFooterProps): ReactNode {
  return (
    <div className="mt-auto flex w-full pt-space-md">
      {renderTertiaryAction?.()}
      <div className="ml-auto flex flex-wrap gap-sm">
        {renderSecondaryAction?.()}
        {renderPrimaryAction()}
      </div>
    </div>
  );
}

export function FormSplitContent({ children }: { children: ReactNode }): ReactNode {
  return (
    <Columns gap="md" collapseBelow="md">
      {Children.map(children, (child) => (
        <Column>{child}</Column>
      ))}
    </Columns>
  );
}

export function FormControlGroup({
  children,
  layout = "horizontal",
}: {
  children: ReactNode;
  layout?: "horizontal" | "vertical";
}): ReactNode {
  return (
    <div
      className={twJoin(
        "flex",
        layout === "horizontal" ? "flex-row form-control-group-horizontal-gap" : "flex-col gap-sm",
      )}
    >
      {children}
    </div>
  );
}
